import { styled, Box, Typography, Button, Link } from "@mui/material";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius, appThemeShadow, appThemeSize } from "styles/appThemeTokens";

const uiFont = appThemeFonts.family.ui;

export const ImportsPageRoot = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  gridTemplateRows: "minmax(0, 1fr)",
  gap: theme.spacing(2),
  alignItems: "stretch",
  marginTop: theme.spacing(1),
  fontFamily: uiFont,
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "minmax(0, 1fr)",
    gridTemplateRows: "none",
    overflow: "visible",
  },
}));

export const ImportCardShell = styled(Box)(() => ({
  backgroundColor: appThemeColors.background.paper,
  border: `1px solid ${appThemeColors.border.default}`,
  borderRadius: appThemeRadius.lg,
  overflow: "hidden",
  boxShadow: appThemeShadow.card,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
}));

export const ImportPrimaryCard = styled(ImportCardShell)(() => ({
  alignSelf: "start",
}));

export const ImportStatusCard = styled(ImportCardShell)(({ theme }) => ({
  minHeight: 0,
  height: "100%",
  maxHeight: "100%",
  [theme.breakpoints.down("md")]: {
    height: "auto",
    maxHeight: "min(70vh, 720px)",
  },
}));

export const ImportStatusCardBody = styled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
}));

export const CardHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  padding: "14px 16px",
  borderBottom: `1px solid ${appThemeColors.background.soft}`,
  flexShrink: 0,
}));

export const RunCountBadge = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.sm,
  fontWeight: appThemeFonts.weight.medium,
  color: appThemeColors.text.muted,
  margin: 0,
  padding: "3px 10px",
  border: `1px solid ${appThemeColors.border.default}`,
  borderRadius: appThemeRadius.pill,
  backgroundColor: appThemeColors.background.paper,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
}));

export const CardBody = styled(Box)(() => ({
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
}));

export const CardTitle = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontWeight: appThemeFonts.weight.semibold,
  fontSize: appThemeFonts.size.lg,
  lineHeight: 1.3,
  color: appThemeColors.text.heading,
  margin: 0,
}));

export const FieldLabel = styled(Typography)(() => ({
  display: "block",
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.mdPlus,
  fontWeight: appThemeFonts.weight.medium,
  color: appThemeColors.text.primary,
  marginBottom: 7,
}));

export const FileRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "stretch",
  gap: theme.spacing(1.5),
  width: "100%",
  flexWrap: "nowrap",
  [theme.breakpoints.down("sm")]: {
    flexWrap: "wrap",
  },
}));

export const ActionRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 10,
  width: "100%",
  flexWrap: "wrap",
}));

export const FileNameBox = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  height: appThemeSize.actionButtonHeight,
  border: `1px solid ${appThemeColors.border.strong}`,
  borderRadius: appThemeRadius.md,
  padding: "0 11px",
  backgroundColor: appThemeColors.background.paper,
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.mdPlus,
  color: appThemeColors.text.muted,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const UploadButton = styled(Button)(() => ({
  textTransform: "none",
  fontFamily: uiFont,
  fontWeight: appThemeFonts.weight.semibold,
  fontSize: appThemeFonts.size.base,
  height: appThemeSize.actionButtonHeight,
  minHeight: appThemeSize.actionButtonHeight,
  padding: "0 16px",
  borderRadius: appThemeRadius.pill,
  boxShadow: "none",
  flexShrink: 0,
  gap: 7,
  "& .MuiButton-startIcon": {
    marginRight: 0,
    marginLeft: 0,
  },
  "&.MuiButton-contained": {
    backgroundColor: appThemeColors.text.lightBlue,
    color: appThemeColors.neutral.white,
    border: 0,
    "&:hover": {
      boxShadow: "none",
      backgroundColor: appThemeColors.text.heading,
    },
    "&.Mui-disabled": {
      backgroundColor: appThemeColors.background.fill,
      color: appThemeColors.text.faint,
      opacity: 0.7,
    },
  },
  "&.MuiButton-outlined": {
    backgroundColor: appThemeColors.background.paper,
    border: `1px solid ${appThemeColors.border.strong}`,
    color: appThemeColors.text.primary,
    "&:hover": {
      boxShadow: "none",
      backgroundColor: appThemeColors.background.kbd,
      borderColor: appThemeColors.text.lightBlue,
    },
    "&.Mui-disabled": {
      backgroundColor: appThemeColors.background.paper,
      borderColor: appThemeColors.border.default,
      color: appThemeColors.text.faint,
      opacity: 0.55,
    },
  },
  "&:focus-visible": {
    outline: `2px solid ${appThemeColors.text.lightBlue}`,
    outlineOffset: 2,
  },
}));

export const ValidateButton = styled(UploadButton)(() => ({
  "&.MuiButton-contained": {
    backgroundColor: appThemeColors.text.heading,
    color: appThemeColors.neutral.white,
    border: 0,
    "&:hover": {
      boxShadow: "none",
      backgroundColor: appThemeColors.text.primary,
    },
    "&.Mui-disabled": {
      backgroundColor: appThemeColors.background.fill,
      color: appThemeColors.text.faint,
      opacity: 0.7,
    },
  },
}));

export const ClearLink = styled(Link)(() => ({
  border: 0,
  background: "none",
  color: appThemeColors.text.lightBlue,
  textDecoration: "none",
  cursor: "pointer",
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.base,
  fontWeight: appThemeFonts.weight.semibold,
  padding: "0 10px",
  height: appThemeSize.actionButtonHeight,
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  "&:hover": {
    color: appThemeColors.text.heading,
    textDecoration: "underline",
  },
}));

export const ImportRunItem = styled(Box)(() => ({
  borderBottom: `1px solid ${appThemeColors.background.soft}`,
  "&:last-child": {
    borderBottom: 0,
  },
}));

export const RunHeaderRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "nowrap",
  padding: "13px 16px",
  minHeight: appThemeSize.tableRowHeight,
  boxSizing: "border-box",
}));

export const ImportRunFile = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.base,
  fontWeight: appThemeFonts.weight.semibold,
  color: appThemeColors.text.primary,
  flex: "1 1 0",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  margin: 0,
  lineHeight: 1.35,
  paddingRight: 4,
}));

export const RunSummaryMetrics = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.md,
  fontWeight: appThemeFonts.weight.regular,
  color: appThemeColors.text.muted,
  margin: 0,
  flexShrink: 0,
  whiteSpace: "nowrap",
  lineHeight: 1.35,
}));

export const RunToggleButton = styled("button")(() => ({
  border: 0,
  background: "none",
  padding: 0,
  marginLeft: 10,
  cursor: "pointer",
  color: appThemeColors.text.lightBlue,
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.base,
  fontWeight: appThemeFonts.weight.semibold,
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  lineHeight: 1.35,
  "&:hover": {
    textDecoration: "underline",
  },
  "&:focus-visible": {
    outline: `2px solid ${appThemeColors.text.lightBlue}`,
    outlineOffset: 2,
  },
}));

export const ExpandedRunPanel = styled(Box)(() => ({
  padding: "0 16px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 22,
}));

export const ResultsText = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.md,
  fontWeight: appThemeFonts.weight.regular,
  color: appThemeColors.text.muted,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: "100%",
}));

export const MetricsGrid = styled(Box)(() => ({
  display: "inline-grid",
  gridAutoFlow: "column",
  gridAutoColumns: "max-content",
  alignSelf: "flex-start",
  width: "max-content",
  maxWidth: "100%",
  columnGap: "32px",
  rowGap: "12px",
  padding: "0 0 2px",
}));

export const MetricBlock = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "5px",
  minWidth: "max-content",
}));

export const MetricValue = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: "27px",
  fontWeight: appThemeFonts.weight.bold,
  lineHeight: 1.05,
  margin: 0,
  letterSpacing: "-0.025em",
  color: appThemeColors.text.heading,
  textAlign: "center",
  width: "100%",
}));

export const MetricLabel = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.md,
  fontWeight: appThemeFonts.weight.regular,
  color: appThemeColors.text.muted,
  margin: 0,
  lineHeight: 1.4,
  textAlign: "center",
  width: "100%",
}));

export const CodesSectionContainer = styled(Box)(() => ({
  border: `1px solid ${appThemeColors.border.default}`,
  borderRadius: appThemeRadius.md,
  backgroundColor: appThemeColors.background.paper2,
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
}));

export const CodesSectionHeader = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  paddingBottom: 10,
  borderBottom: `1px solid ${appThemeColors.border.secondary}`,
}));

export const CodesSectionTitle = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.base,
  fontWeight: appThemeFonts.weight.semibold,
  color: appThemeColors.text.heading,
  margin: 0,
  lineHeight: 1.35,
}));

export const CodesGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(2.5),
  columnGap: theme.spacing(3.5),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
}));

export const CodeEntry = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 8,
  minWidth: 0,
  padding: "4px 0 6px",
}));

export const CodeEntrySummary = styled(Typography)(() => ({
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.md,
  lineHeight: 1.5,
  margin: 0,
  wordBreak: "normal",
  overflowWrap: "anywhere",
}));

export const CodeEntryCountPart = styled("span")(() => ({
  fontWeight: appThemeFonts.weight.semibold,
  color: appThemeColors.text.primary,
}));

export const CodeEntryDescPart = styled("span")(() => ({
  fontWeight: appThemeFonts.weight.regular,
  color: appThemeColors.status.neutral.color,
}));

export const CodeLookupNote = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 5,
  paddingLeft: 1,
  color: appThemeColors.text.muted,
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.sm,
  lineHeight: 1.5,
  marginTop: 4,
  maxWidth: "100%",
  "& .lookup-icon": {
    color: appThemeColors.text.lightBlue,
    flexShrink: 0,
    marginTop: 1,
  },
}));

export const DownloadReportFooter = styled(Box)(() => ({
  display: "flex",
  justifyContent: "flex-end",
  paddingTop: 4,
  marginTop: 2,
}));

export const DownloadReportButton = styled("button")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  height: appThemeSize.actionButtonHeight,
  border: `1px solid ${appThemeColors.border.default}`,
  borderRadius: appThemeRadius.pill,
  backgroundColor: appThemeColors.background.paper,
  color: appThemeColors.text.primary,
  cursor: "pointer",
  fontFamily: uiFont,
  fontSize: appThemeFonts.size.base,
  fontWeight: appThemeFonts.weight.semibold,
  padding: "0 14px",
  lineHeight: 1.2,
  "&:hover": {
    borderColor: appThemeColors.text.lightBlue,
    color: appThemeColors.text.lightBlue,
  },
  "&:focus-visible": {
    outline: `2px solid ${appThemeColors.text.lightBlue}`,
    outlineOffset: 2,
  },
}));

export const ScreenReaderAlert = styled("div")(() => ({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
}));

export const StatusBody = styled(Typography, {
  shouldForwardProp: (p) => p !== "$tier",
})(({ $tier = "neutral" }) => {
  const isNeutral = $tier === "neutral";
  const isError = $tier === "error";
  const isRunSummary = $tier === "run";
  return {
    fontFamily: uiFont,
    fontSize: isRunSummary
      ? appThemeFonts.size.md
      : isNeutral
        ? appThemeFonts.size.base
        : appThemeFonts.size.mdPlus,
    lineHeight: 1.5,
    color: isRunSummary
      ? appThemeColors.text.muted
      : isNeutral
        ? appThemeColors.text.muted
        : isError
          ? appThemeColors.semantic.danger
          : appThemeColors.text.primary,
    whiteSpace: "pre-wrap",
    flex: 1,
    minHeight: 0,
    padding: isNeutral
      ? "34px 16px"
      : isRunSummary
        ? "0 16px 16px"
        : "13px 16px 16px",
    textAlign: isNeutral ? "center" : "left",
  };
});
